import React from "react";
import { load } from "opentype.js";
import { getRandomString } from "../_helpers/utils";

const useTextArtTransform = () => {
  let waveFont = null;

  const trimSVG = svg => {
    const bbox = svg.getBBox();
    const { x, y, width, height } = bbox;

    svg.setAttribute("viewBox", `${x} ${y} ${width} ${height + 2}`);
    svg.setAttribute("width", width);
    svg.setAttribute("height", height + 2);
  };

  async function loadWaveFont(availableFontLists) {
    // if (waveFont) return waveFont; // already loaded
    try {
      let fontFam = document.querySelector(".text-content").style.fontFamily.replace(/^["']|["']$/g, "");
      let fontUrl, fontObj;

      if (document.querySelector(".text-content").style.fontWeight === "700") {
        fontObj = availableFontLists.find(f => f.name === `${fontFam} Bold`);
      } else {
        fontObj = availableFontLists.find(f => f.name === fontFam);
      }

      if (fontObj) {
        if (fontObj.source === "Custom") {
          fontUrl = fontObj.url?.ttf;
        } else {
          fontUrl = fontObj.url;
        }
      }

      waveFont = await load(fontUrl);

      return waveFont;
    } catch (err) {
      console.error("Font load error", err);
      return null;
    }
  }

  const createPlainText = (updateContext, setTextHtml, setLetterSpacing) => {
    let container = document.querySelector(".text-content");
    let text = container.getAttribute("data-content");

    container.innerHTML = text;

    // Add data attributes
    let data = {
      "data-fill-type": "solid",
      "data-color-count": 1,
      "data-color-one": container.getAttribute("data-color-one") ?? "#72ac4d",
      "data-transform-type": "Path 1",
    };

    Object.entries(data).forEach(([prop, value]) => {
      container.setAttribute(prop, value);
    });

    // Remove some data attributes
    let attributesToRemove = [
      "data-color-one-range",
      "data-color-two-range",
      "data-color-three-range",
      "data-color-two",
      "data-color-three",
      "data-grad-degree",
      "data-grad-scolor",
      "data-grad-spercen",
      "data-grad-ecolor",
      "data-grad-epercen",
      "data-transform-value",
      "data-viewBox-value",
    ];
    attributesToRemove.forEach(attr => {
      container.removeAttribute(attr);
    });

    let cssstyle = {
      color: container.getAttribute("data-color-one") || "#72ac4d",
      "background-image": "",
      "-webkit-text-fill-color": "",
      "background-clip": "",
      "-webkit-background-clip": "",
      "font-family": container.style.fontFamily,
      "font-size": container.style.fontSize,
      "font-weight": container.style.fontWeight,
      "letter-spacing": container.style.letterSpacing,
    };

    // if outline apllied
    if (container.getAttribute("data-outline")) {
      cssstyle = {
        ...cssstyle,
        "-webkit-text-stroke": `${container.getAttribute("data-outline-width")}px ${container.getAttribute(
          "data-outline-color"
        )}`,
      };
    }

    // Clear all inline styles
    container.removeAttribute("style");

    // Apply new ones
    Object.entries(cssstyle).forEach(([prop, value]) => {
      container.style.setProperty(prop, value);
    });

    setLetterSpacing(container.style.letterSpacing ? parseInt(container.style.letterSpacing) : 0);
    updateContext && setTextHtml(document.querySelector(".text-content"));
  };

  const createCircleTextSVG = (sliderValue, updateContext, setTextHtml) => {
    const xmlns = "http://www.w3.org/2000/svg";

    const minRadius = 50;
    const svgSize = 500;
    const padding = 40;
    const startAngle = -180;
    const center = svgSize / 2;

    const pane = document.querySelector(".right-content");
    let targetContainer = document.querySelector(".text-content");
    let text = targetContainer.getAttribute("data-content");

    //font existing Styles
    let fontSize = parseInt(targetContainer.style.fontSize);
    let fontFamily = targetContainer.style.fontFamily;
    let fontWeight = targetContainer.style.fontWeight;
    let fontColor = targetContainer.getAttribute("data-color-one") || "#72ac4d";

    // --- measure text width ---
    const measureCanvas = document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    ctx.font = `${fontSize}px ${fontFamily}`;
    const textWidth = ctx.measureText(text).width;

    // =============================
    // 🔥 MODIFIED RADIUS LOGIC ONLY
    // =============================

    const neededCircumference = textWidth + padding;
    const calculatedMinRadius = neededCircumference / (2 * Math.PI);

    const finalMinRadius = Math.max(minRadius, calculatedMinRadius);

    const extraRadius = parseFloat(sliderValue) || 0;
    let radius = finalMinRadius + extraRadius;

    // =============================
    // 🔥 FIT CHECK & AUTO SCALE (your original logic preserved)
    // =============================
    const paneRect = pane.getBoundingClientRect();
    const maxAllowedRadius = Math.min(paneRect.width, paneRect.height) / 2 - fontSize;

    if (radius > maxAllowedRadius) {
      const scale = maxAllowedRadius / radius;
      radius *= scale;
      fontSize *= scale;
    }

    // create <svg>
    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", svgSize);
    svg.setAttribute("height", svgSize);
    svg.setAttribute("viewBox", `0 0 ${svgSize} ${svgSize}`);

    // guide circle (optional)
    const guide = document.createElementNS(xmlns, "circle");
    guide.setAttribute("cx", center);
    guide.setAttribute("cy", center);
    guide.setAttribute("r", radius);
    guide.setAttribute("fill", "none");
    svg.appendChild(guide);

    // path for circle
    const path = document.createElementNS(xmlns, "path");
    const pathId = "circlePath-" + getRandomString(8);
    path.setAttribute("id", pathId);
    path.setAttribute("fill", "none");

    const d = `
    M ${center} ${center - radius}
    A ${radius} ${radius} 0 1 1 ${center - 0.01} ${center - radius}
  `;
    path.setAttribute("d", d.trim());
    svg.appendChild(path);

    // text + textPath
    const textElem = document.createElementNS(xmlns, "text");
    textElem.setAttribute("font-size", fontSize);
    textElem.setAttribute("font-family", fontFamily);
    textElem.setAttribute("fill", fontColor);
    textElem.setAttribute("font-weight", fontWeight);

    //if outline applied
    if (targetContainer?.getAttribute("data-outline")) {
      textElem.setAttribute("stroke", targetContainer.getAttribute("data-outline-color"));
      textElem.setAttribute("stroke-width", parseInt(targetContainer.getAttribute("data-outline-width")));
    }

    // rotate text
    textElem.setAttribute("transform", `rotate(${startAngle}, ${center}, ${center})`);

    const textPath = document.createElementNS(xmlns, "textPath");
    textPath.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${pathId}`);
    textPath.setAttribute("startOffset", "50%");
    textPath.setAttribute("text-anchor", "middle");
    textPath.textContent = text;

    textElem.appendChild(textPath);
    svg.appendChild(textElem);

    targetContainer.innerHTML = "";
    targetContainer.appendChild(svg);

    // Add data attributes (PRESERVED)
    let data = {
      "data-fill-type": "solid",
      "data-color-count": 1,
      "data-color-one": targetContainer.getAttribute("data-color-one") ?? "#72ac4d",
      "data-transform-type": "Path 2",
      "data-radius-value": sliderValue,
    };

    Object.entries(data).forEach(([prop, value]) => {
      targetContainer.setAttribute(prop, value);
    });

    // Remove some data attributes (PRESERVED)
    let attributesToRemove = [
      "data-color-one-range",
      "data-color-two-range",
      "data-color-three-range",
      "data-color-two",
      "data-color-three",
      "data-grad-degree",
      "data-grad-scolor",
      "data-grad-spercen",
      "data-grad-ecolor",
      "data-grad-epercen",
      "data-transform-value",
      "data-viewBox-value",
    ];

    attributesToRemove.forEach(attr => {
      targetContainer.removeAttribute(attr);
    });

    document.querySelector(".text-content").style.letterSpacing = "0px";
    document.querySelector(".text-content").style.display = "block";

    updateContext && setTextHtml(targetContainer);
  };

  const createHalfCircleTextSVG_Original = (sliderValue = 0, isTop = true, updateContext, setTextHtml) => {
    const xmlns = "http://www.w3.org/2000/svg";

    const minRadius = 50;
    const padding = 40;

    const pane = document.querySelector(".TMright-pane");
    const container = document.querySelector(".text-content");

    if (!container || !pane) return;

    const text = container.getAttribute("data-content") || "";

    let fontSize = parseFloat(container.style.fontSize) || 20;
    const fontFamily = container.style.fontFamily || "Arial";
    const fontWeight = container.style.fontWeight || "normal";
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    // =============================
    // Measure text width
    // =============================

    const measureCanvas = document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");

    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

    const textWidth = ctx.measureText(text).width;

    // =============================
    // Calculate BASE radius (your original logic)
    // =============================

    let baseRadius = minRadius;

    const neededArcLength = textWidth + padding;
    const halfCircumference = Math.PI * baseRadius;

    if (neededArcLength > halfCircumference) {
      baseRadius = neededArcLength / Math.PI;
    }

    // Safety minimum (your original protection)
    const minSafeRadius = fontSize * 3.5;

    if (baseRadius < minSafeRadius) {
      baseRadius = minSafeRadius;
    }

    // =============================
    // 🔥 CURVATURE CONTROL (NEW)
    // =============================

    const slider = parseFloat(sliderValue) || 0;

    /*
    slider = 0   → normal
    slider = 50  → more curve
    slider = 100 → maximum curve
  */

    const curveFactor = 1 - slider / 100;

    let radius = baseRadius * (0.5 + curveFactor * 0.5);

    // =============================
    // Keep inside pane (YOUR ORIGINAL LOGIC INTACT)
    // =============================

    const paneRect = pane.getBoundingClientRect();

    const maxAllowedRadius = Math.min(paneRect.width / 2, paneRect.height);

    if (radius > maxAllowedRadius) {
      radius = maxAllowedRadius;
    }

    // =============================
    // SVG size
    // =============================

    const svgWidth = radius * 2;
    const svgHeight = radius + fontSize * 2;

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("viewBox", `0 0 ${svgWidth} ${svgHeight}`);
    svg.style.width = "100%";
    svg.style.height = "auto";
    svg.style.display = "block";
    svg.style.overflow = "visible";

    // =============================
    // Arc path
    // =============================

    const startX = 0;
    const startY = radius;
    const endX = radius * 2;
    const endY = radius;

    const sweepFlag = isTop ? 0 : 1;

    const d = `
    M ${startX} ${startY}
    A ${radius} ${radius} 0 1 ${sweepFlag} ${endX} ${endY}
  `;

    const path = document.createElementNS(xmlns, "path");
    const pathId = "halfCirclePath-" + Math.random().toString(36).slice(2);
    path.setAttribute("id", pathId);
    path.setAttribute("fill", "none");
    path.setAttribute("d", d.trim());
    svg.appendChild(path);

    // =============================
    // Text
    // =============================

    const textElem = document.createElementNS(xmlns, "text");
    textElem.setAttribute("font-size", fontSize);
    textElem.setAttribute("font-family", fontFamily);
    textElem.setAttribute("font-weight", fontWeight);
    textElem.setAttribute("fill", fontColor);

    // if outline applied
    if (container.getAttribute("data-outline")) {
      textElem.setAttribute("stroke", container.getAttribute("data-outline-color"));
      textElem.setAttribute("stroke-width", parseInt(container.getAttribute("data-outline-width")) || 1);
    }

    const textPath = document.createElementNS(xmlns, "textPath");
    textPath.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${pathId}`);
    textPath.setAttribute("startOffset", "50%");
    textPath.setAttribute("text-anchor", "middle");
    textPath.textContent = text;

    textElem.appendChild(textPath);
    svg.appendChild(textElem);

    // =============================
    // Replace container content
    // =============================

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", isTop ? "Path 4" : "Path 3");
    container.setAttribute("data-radius-value", sliderValue);
    container.removeAttribute("data-transform-value");
    container.removeAttribute("data-viewBox-value");
    container.style.letterSpacing = "0px";

    // =============================
    // Keep inside pane width
    // =============================

    const parentWidth = pane.clientWidth;
    svg.style.maxWidth = parentWidth + "px";

    container.setAttribute("data-max-width", parentWidth);

    // =============================
    // Trim SVG (your original logic)
    // =============================

    trimSVG(svg);

    // =============================
    // Update context
    // =============================

    if (updateContext && setTextHtml) {
      setTextHtml(container);
    }
  };

  const createHalfCircleTextSVG_Safari = (sliderValue = 0, isTop = true, updateContext, setTextHtml) => {
    const xmlns = "http://www.w3.org/2000/svg";

    const minRadius = 50;
    const padding = 40;

    const svgSize = 500;
    const center = svgSize / 2;

    const pane = document.querySelector(".TMright-pane");
    const container = document.querySelector(".text-content");

    if (!container || !pane) return;

    const text = container.getAttribute("data-content") || "";

    let fontSize = parseFloat(container.style.fontSize) || 20;
    const fontFamily = container.style.fontFamily || "Arial";
    const fontWeight = container.style.fontWeight || "normal";
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    // measure text
    const measureCanvas = document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    const textWidth = ctx.measureText(text).width;

    // base radius
    let baseRadius = minRadius;

    const neededArcLength = textWidth + padding;
    const halfCircumference = Math.PI * baseRadius;

    if (neededArcLength > halfCircumference) {
      baseRadius = neededArcLength / Math.PI;
    }

    const minSafeRadius = fontSize * 3.5;
    if (baseRadius < minSafeRadius) baseRadius = minSafeRadius;

    const slider = parseFloat(sliderValue) || 0;
    const curveFactor = 1 - slider / 100;

    let radius = baseRadius * (0.5 + curveFactor * 0.5);

    const paneRect = pane.getBoundingClientRect();
    const maxAllowedRadius = Math.min(paneRect.width, paneRect.height) / 2 - fontSize;

    if (radius > maxAllowedRadius) {
      const scale = maxAllowedRadius / radius;
      radius *= scale;
      fontSize *= scale;
    }

    // create svg (FIXED SIZE like circle version)
    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", svgSize);
    svg.setAttribute("height", svgSize);
    svg.setAttribute("viewBox", `0 0 ${svgSize} ${svgSize}`);

    // path
    const path = document.createElementNS(xmlns, "path");
    const pathId = "halfCirclePath-" + Math.random().toString(36).slice(2);

    const startX = center - radius;
    const endX = center + radius;
    const y = center;

    const sweepFlag = isTop ? 0 : 1;

    const d = `
    M ${startX} ${y}
    A ${radius} ${radius} 0 0 ${sweepFlag} ${endX} ${y}
  `;

    path.setAttribute("id", pathId);
    path.setAttribute("fill", "none");
    path.setAttribute("d", d.trim());

    svg.appendChild(path);

    // text
    const textElem = document.createElementNS(xmlns, "text");
    textElem.setAttribute("font-size", fontSize);
    textElem.setAttribute("font-family", fontFamily);
    textElem.setAttribute("font-weight", fontWeight);
    textElem.setAttribute("fill", fontColor);

    if (container.getAttribute("data-outline")) {
      textElem.setAttribute("stroke", container.getAttribute("data-outline-color"));
      textElem.setAttribute("stroke-width", parseInt(container.getAttribute("data-outline-width")) || 1);
    }

    const textPath = document.createElementNS(xmlns, "textPath");
    textPath.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${pathId}`);

    textPath.setAttribute("startOffset", "50%");
    textPath.setAttribute("text-anchor", "middle");
    textPath.textContent = text;

    textElem.appendChild(textPath);
    svg.appendChild(textElem);

    container.innerHTML = "";
    container.appendChild(svg);

    container.setAttribute("data-transform-type", isTop ? "Path 4" : "Path 3");
    container.setAttribute("data-radius-value", sliderValue);

    container.removeAttribute("data-transform-value");
    container.removeAttribute("data-viewBox-value");

    container.style.letterSpacing = "0px";

    trimSVG(svg);

    if (updateContext && setTextHtml) {
      setTextHtml(container);
    }
  };

  const createHalfCircleTextSVG = (sliderValue = 0, isTop = true, updateContext, setTextHtml) => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari) {
      createHalfCircleTextSVG_Safari(sliderValue, isTop, updateContext, setTextHtml);
    } else {
      createHalfCircleTextSVG_Original(sliderValue, isTop, updateContext, setTextHtml);
    }
  };

  async function createWarpedTextSVG1(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists);
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0; // start at left
    const baselineY = fontSize * 1.2; // dynamic baseline
    const bend = 0; // scale bend with font size
    const stretch = parseFloat((parseFloat(sliderValue) / 100) * 2);

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2", "y", "y1", "y2"].forEach(k => {
          if (c[k] !== undefined) {
            if (k.startsWith("x")) {
              minX = Math.min(minX, c[k]);
              maxX = Math.max(maxX, c[k]);
            }
            if (k.startsWith("y")) {
              minY = Math.min(minY, c[k]);
              maxY = Math.max(maxY, c[k]);
            }
          }
        });
      });
      return {
        minX: isFinite(minX) ? minX : 0,
        maxX: isFinite(maxX) ? maxX : 0,
        minY: isFinite(minY) ? minY : 0,
        maxY: isFinite(maxY) ? maxY : 0,
      };
    };
    const bounds = computeBounds(cmds);

    const warpPoint = (x, y, bounds, baselineY, bend, stretch) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const centerX = (bounds.minX + bounds.maxX) / 2;
      const u = (x - bounds.minX) / width;
      const peak = Math.max(0, 1 - Math.pow(2 * u - 1, 2));
      const displacement = -bend * peak;
      const scaleY = 1 + stretch * peak;
      const maxSqueeze = 0.22;
      const squeezeFactor = 1 - maxSqueeze * peak;
      const sx = centerX + (x - centerX) * squeezeFactor;
      const slope = (8 * bend * (u - 0.5)) / width;
      const shearStrength = 0.45;
      const shearOffset = (y - baselineY) * slope * shearStrength;
      const newY = baselineY + (y - baselineY) * scaleY + displacement;
      const newX = sx + shearOffset;
      return { x: newX, y: newY };
    };

    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds, baselineY, bend, stretch);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds, baselineY, bend, stretch);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds, baselineY, bend, stretch);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    const newBounds = computeBounds(cmds);
    const padding = 18;
    const vbX = Math.floor(newBounds.minX - padding);
    const vbY = Math.floor(newBounds.minY - padding);
    const vbW = Math.ceil(newBounds.maxX - newBounds.minX + padding * 2);
    const vbH = Math.ceil(newBounds.maxY - newBounds.minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);

    // Add data attributes
    let data = {
      "data-fill-type": "solid",
      "data-color-count": 1,
      "data-color-one": container.getAttribute("data-color-one") ?? "#72ac4d",
      "data-transform-type": "Warp 1",
      "data-transform-value": sliderValue,
      "data-viewBox-value": `${vbX} ${vbY} ${vbW} ${vbH}`,
    };
    Object.entries(data).forEach(([prop, value]) => {
      container.setAttribute(prop, value);
    });

    // Remove some data attributes
    let attributesToRemove = [
      "data-color-one-range",
      "data-color-two-range",
      "data-color-three-range",
      "data-color-two",
      "data-color-three",
      "data-grad-degree",
      "data-grad-scolor",
      "data-grad-spercen",
      "data-grad-ecolor",
      "data-grad-epercen",
    ];
    attributesToRemove.forEach(attr => {
      container.removeAttribute(attr);
    });

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  async function createWarpedTextSVG2(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists);
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0;
    const baselineY = fontSize * 1.2;

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2", "y", "y1", "y2"].forEach(k => {
          if (c[k] !== undefined) {
            if (k.startsWith("x")) {
              minX = Math.min(minX, c[k]);
              maxX = Math.max(maxX, c[k]);
            }
            if (k.startsWith("y")) {
              minY = Math.min(minY, c[k]);
              maxY = Math.max(maxY, c[k]);
            }
          }
        });
      });
      return { minX, maxX, minY, maxY };
    };
    const bounds = computeBounds(cmds);

    const warpPoint = (x, y, bounds, baselineY, slider) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const u = (x - bounds.minX) / width; // normalize across width
      const peak = Math.max(0, 1 - Math.pow(2 * u - 1, 2));

      // Only stretch downward from the top
      const topY = bounds.minY; // top of text
      const t = (y - topY) / (bounds.maxY - topY); // relative vertical position 0=top, 1=bottom

      const extra = slider * peak * t; // stronger effect at bottom
      return { x, y: y + extra };
    };

    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds, baselineY, sliderValue);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds, baselineY, sliderValue);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds, baselineY, sliderValue);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    const newBounds = computeBounds(cmds);
    const padding = 20;
    const vbX = Math.floor(newBounds.minX - padding);
    const vbY = Math.floor(newBounds.minY - padding);
    const vbW = Math.ceil(newBounds.maxX - newBounds.minX + padding * 2);
    const vbH = Math.ceil(newBounds.maxY - newBounds.minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", "Warp 2");
    container.setAttribute("data-transform-value", sliderValue);
    container.setAttribute("data-viewBox-value", `${vbX} ${vbY} ${vbW} ${vbH}`);

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  async function createWarpedTextSVG3(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists);
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0;
    const baselineY = fontSize * 1.2;

    const fixedCurve = 6;

    // remap slider 0–100 into 0–50
    const effectiveSlider = (sliderValue / 100) * 50;
    const stretch = effectiveSlider / 20;

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2", "y", "y1", "y2"].forEach(k => {
          if (c[k] !== undefined) {
            if (k.startsWith("x")) {
              minX = Math.min(minX, c[k]);
              maxX = Math.max(maxX, c[k]);
            }
            if (k.startsWith("y")) {
              minY = Math.min(minY, c[k]);
              maxY = Math.max(maxY, c[k]);
            }
          }
        });
      });
      return {
        minX: isFinite(minX) ? minX : 0,
        maxX: isFinite(maxX) ? maxX : 0,
        minY: isFinite(minY) ? minY : 0,
        maxY: isFinite(maxY) ? maxY : 0,
      };
    };

    const bounds = computeBounds(cmds);

    const warpPoint = (x, y, bounds, baselineY, stretch, fixedCurve) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const u = (x - bounds.minX) / width;
      const centerU = 0.5;

      const curve = fixedCurve * (1 - Math.pow(2 * u - 1, 2));

      const distFromCenter = Math.abs(u - centerU) * 2;
      const scaleY = 1 + stretch * distFromCenter;

      const newX = x;
      const newY = baselineY + (y - baselineY) * scaleY + curve;
      return { x: newX, y: newY };
    };

    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds, baselineY, stretch, fixedCurve);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds, baselineY, stretch, fixedCurve);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds, baselineY, stretch, fixedCurve);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    const newBounds = computeBounds(cmds);
    const padding = 18;
    const vbX = Math.floor(newBounds.minX - padding);
    const vbY = Math.floor(newBounds.minY - padding);
    const vbW = Math.ceil(newBounds.maxX - newBounds.minX + padding * 2);
    const vbH = Math.ceil(newBounds.maxY - newBounds.minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", "Warp 3");
    container.setAttribute("data-transform-value", sliderValue);
    container.setAttribute("data-viewBox-value", `${vbX} ${vbY} ${vbW} ${vbH}`);

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  async function createWarpedTextSVG4(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists);
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0;
    const baselineY = fontSize * 1.2;

    const effectiveSlider = (sliderValue / 100) * 50;
    const stretch = effectiveSlider / 20;
    const curveStrength = 10;

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2", "y", "y1", "y2"].forEach(k => {
          if (c[k] !== undefined) {
            if (k.startsWith("x")) {
              minX = Math.min(minX, c[k]);
              maxX = Math.max(maxX, c[k]);
            }
            if (k.startsWith("y")) {
              minY = Math.min(minY, c[k]);
              maxY = Math.max(maxY, c[k]);
            }
          }
        });
      });
      return { minX, maxX, minY, maxY };
    };

    const bounds = computeBounds(cmds);
    const globalTopY = bounds.minY; // Use the absolute top of all glyphs

    const warpPoint = (x, y, bounds, globalTopY, stretch, curveStrength) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const u = (x - bounds.minX) / width;
      const centerU = 0.5;

      // Create a curved top baseline
      const curve = curveStrength * (1 - Math.pow(2 * u - 1, 2));
      const topY = globalTopY;

      const relY = y - globalTopY;
      const scaleY = 1 + stretch * Math.abs(u - centerU) * 2;

      const newX = x;
      const newY = topY + relY * scaleY;

      return { x: newX, y: newY };
    };

    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds, globalTopY, stretch, curveStrength);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds, globalTopY, stretch, curveStrength);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds, globalTopY, stretch, curveStrength);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    const newBounds = computeBounds(cmds);
    const padding = 18;
    const vbX = Math.floor(newBounds.minX - padding);
    const vbY = Math.floor(newBounds.minY - padding);
    const vbW = Math.ceil(newBounds.maxX - newBounds.minX + padding * 2);
    const vbH = Math.ceil(newBounds.maxY - newBounds.minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", "Warp 4");
    container.setAttribute("data-transform-value", sliderValue);
    container.setAttribute("data-viewBox-value", `${vbX} ${vbY} ${vbW} ${vbH}`);

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  async function createWarpedTextSVG5(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists); // Your font loader
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0;
    const baselineY = fontSize * 1.2;

    // Control height stretch from left (1x) to right (1 + factor)
    const stretchFactor = 1 + (sliderValue / 100) * 2; // e.g., from 1 to 3

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2"].forEach(k => {
          if (c[k] !== undefined) {
            minX = Math.min(minX, c[k]);
            maxX = Math.max(maxX, c[k]);
          }
        });
      });
      return { minX, maxX };
    };

    const bounds = computeBounds(cmds);

    const warpPoint = (x, y, bounds) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const u = (x - bounds.minX) / width; // 0 (left) → 1 (right)

      const scaleY = 1 + (stretchFactor - 1) * u; // scale grows left to right

      return {
        x: x,
        y: baselineY + (y - baselineY) * scaleY,
      };
    };

    // Apply vertical stretching transformation
    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    // Recompute bounds for viewBox
    const allY = cmds.flatMap(c => [c.y, c.y1, c.y2].filter(v => v !== undefined));
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);
    const padding = 20;
    const vbX = Math.floor(bounds.minX - padding);
    const vbY = Math.floor(minY - padding);
    const vbW = Math.ceil(bounds.maxX - bounds.minX + padding * 2);
    const vbH = Math.ceil(maxY - minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", "Warp 5");
    container.setAttribute("data-transform-value", sliderValue);

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  async function createWarpedTextSVG6(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists); // Your font loader
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0;
    const baselineY = fontSize * 1.2;

    // Control height stretch from right (1x) to left (1 + factor)
    const stretchFactor = 1 + (sliderValue / 100) * 2; // 1 → 3

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2"].forEach(k => {
          if (c[k] !== undefined) {
            minX = Math.min(minX, c[k]);
            maxX = Math.max(maxX, c[k]);
          }
        });
      });
      return { minX, maxX };
    };

    const bounds = computeBounds(cmds);

    const warpPoint = (x, y, bounds) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const u = (x - bounds.minX) / width; // 0 (left) → 1 (right)
      const reverseU = 1 - u; // 1 (left) → 0 (right)

      const scaleY = 1 + (stretchFactor - 1) * reverseU;

      return {
        x: x,
        y: baselineY + (y - baselineY) * scaleY,
      };
    };

    // Apply vertical stretching transformation
    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    // Recompute bounds for viewBox
    const allY = cmds.flatMap(c => [c.y, c.y1, c.y2].filter(v => v !== undefined));
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);
    const padding = 20;
    const vbX = Math.floor(bounds.minX - padding);
    const vbY = Math.floor(minY - padding);
    const vbW = Math.ceil(bounds.maxX - bounds.minX + padding * 2);
    const vbH = Math.ceil(maxY - minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", "Warp 6");
    container.setAttribute("data-transform-value", sliderValue);
    container.setAttribute("data-viewBox-value", `${vbX} ${vbY} ${vbW} ${vbH}`);

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  async function createWarpedTextSVG7(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists);
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0;
    const baselineY = fontSize * 1.2;

    // Slider controls how much downward stretch is applied from left to right
    const stretchFactor = 1 + (sliderValue / 100) * 3; // up to 4× height at far right

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2"].forEach(k => {
          if (c[k] !== undefined) {
            minX = Math.min(minX, c[k]);
            maxX = Math.max(maxX, c[k]);
          }
        });
        ["y", "y1", "y2"].forEach(k => {
          if (c[k] !== undefined) {
            minY = Math.min(minY, c[k]);
          }
        });
      });
      return { minX, maxX, topY: minY };
    };

    const bounds = computeBounds(cmds);

    const warpPoint = (x, y, bounds) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const u = (x - bounds.minX) / width; // 0 (left) → 1 (right)

      const scaleY = 1 + (stretchFactor - 1) * u; // taller as you go right

      return {
        x: x,
        y: bounds.topY + (y - bounds.topY) * scaleY,
      };
    };

    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    // Compute new bounds
    const allY = cmds.flatMap(c => [c.y, c.y1, c.y2].filter(v => v !== undefined));
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);
    const padding = 20;
    const vbX = Math.floor(bounds.minX - padding);
    const vbY = Math.floor(minY - padding);
    const vbW = Math.ceil(bounds.maxX - bounds.minX + padding * 2);
    const vbH = Math.ceil(maxY - minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", "Warp 7");
    container.setAttribute("data-transform-value", sliderValue);
    container.setAttribute("data-viewBox-value", `${vbX} ${vbY} ${vbW} ${vbH}`);

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  async function createWarpedTextSVG8(sliderValue = 50, updateContext, setTextHtml, availableFontLists) {
    const font = await loadWaveFont(availableFontLists);
    if (!font) return;

    const xmlns = "http://www.w3.org/2000/svg";
    const container = document.querySelector(".text-content");
    const text = container.getAttribute("data-content");
    const fontSize = parseInt(container?.style.fontSize) || 60;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    const startX = 0;
    const baselineY = fontSize * 1.2;

    // Stretch factor controlled by slider
    const stretchFactor = 1 + (sliderValue / 100) * 3; // Max 4×

    const pathObj = font.getPath(text, startX, baselineY, fontSize, { hinting: false });
    const cmds = pathObj.commands.map(c => ({ ...c }));

    const computeBounds = cmds => {
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity;
      cmds.forEach(c => {
        ["x", "x1", "x2"].forEach(k => {
          if (c[k] !== undefined) {
            minX = Math.min(minX, c[k]);
            maxX = Math.max(maxX, c[k]);
          }
        });
        ["y", "y1", "y2"].forEach(k => {
          if (c[k] !== undefined) {
            minY = Math.min(minY, c[k]);
          }
        });
      });
      return { minX, maxX, topY: minY };
    };

    const bounds = computeBounds(cmds);

    const warpPoint = (x, y, bounds) => {
      const width = Math.max(1, bounds.maxX - bounds.minX);
      const u = (x - bounds.minX) / width; // 0 (left) → 1 (right)
      const reverseU = 1 - u; // 1 (left) ← 0 (right)

      const scaleY = 1 + (stretchFactor - 1) * reverseU;

      return {
        x: x,
        y: bounds.topY + (y - bounds.topY) * scaleY,
      };
    };

    cmds.forEach(c => {
      if (c.x !== undefined && c.y !== undefined) {
        const p = warpPoint(c.x, c.y, bounds);
        c.x = p.x;
        c.y = p.y;
      }
      if (c.x1 !== undefined && c.y1 !== undefined) {
        const p = warpPoint(c.x1, c.y1, bounds);
        c.x1 = p.x;
        c.y1 = p.y;
      }
      if (c.x2 !== undefined && c.y2 !== undefined) {
        const p = warpPoint(c.x2, c.y2, bounds);
        c.x2 = p.x;
        c.y2 = p.y;
      }
    });

    const commandsToD = cmds =>
      cmds
        .map(c => {
          if (c.type === "M") return `M${c.x},${c.y}`;
          if (c.type === "L") return `L${c.x},${c.y}`;
          if (c.type === "Q") return `Q${c.x1},${c.y1},${c.x},${c.y}`;
          if (c.type === "C") return `C${c.x1},${c.y1},${c.x2},${c.y2},${c.x},${c.y}`;
          if (c.type === "Z") return "Z";
          return "";
        })
        .join(" ");

    // Recompute bounds
    const allY = cmds.flatMap(c => [c.y, c.y1, c.y2].filter(v => v !== undefined));
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);
    const padding = 20;
    const vbX = Math.floor(bounds.minX - padding);
    const vbY = Math.floor(minY - padding);
    const vbW = Math.ceil(bounds.maxX - bounds.minX + padding * 2);
    const vbH = Math.ceil(maxY - minY + padding * 2);

    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", 500);
    svg.setAttribute("height", 300);
    svg.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);

    //if outline applied
    if (container?.getAttribute("data-outline")) {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor} stroke=${container?.getAttribute(
        "data-outline-color"
      )} stroke-width =${parseInt(container?.getAttribute("data-outline-width"))}></path>`;
    } else {
      svg.innerHTML = `<path d="${commandsToD(cmds)}" fill=${fontColor}></path>`;
    }

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-transform-type", "Warp 8");
    container.setAttribute("data-transform-value", sliderValue);
    container.setAttribute("data-viewBox-value", `${vbX} ${vbY} ${vbW} ${vbH}`);

    updateContext && setTextHtml(document.querySelector(".text-content"));
  }

  const createArchTextSVG = (
    archHeight = 150,
    sliderValue,
    direction = "upward",
    updateContext,
    setTextHtml,
    availableFontLists
  ) => {
    const xmlns = "http://www.w3.org/2000/svg";

    const padding = 40;
    const baseY = 150; // baseline for text

    let container = document.querySelector(".text-content");
    let text = container.getAttribute("data-content");
    let fontSize = parseInt(container.style.fontSize) || 30;
    let fontFamily = container.style.fontFamily || "Poppins";
    let fontWeight = container.style.fontWeight;
    const fontColor = container.getAttribute("data-color-one") || "#72ac4d";

    // Measure text width
    const measureWidth = (txt, size, family) => {
      const measureCanvas = document.createElement("canvas");
      const ctx = measureCanvas.getContext("2d");
      ctx.font = `${size}px ${family}`;
      return ctx.measureText(txt).width;
    };

    // Auto-fit font size if text too wide
    const maxWidth = 500;
    let textWidth = measureWidth(text, fontSize, fontFamily);
    if (textWidth > maxWidth) {
      fontSize = Math.max(10, Math.floor((maxWidth / textWidth) * fontSize));
      textWidth = measureWidth(text, fontSize, fontFamily);
    }

    // SVG width
    const svgWidth = textWidth + padding * 2;

    // Path points
    const midX = svgWidth / 2;
    const endX = svgWidth - padding;
    const controlY = direction === "upward" ? baseY - archHeight : baseY + archHeight;

    // === Dynamic height calculation ===
    let minY, maxY, svgHeight;
    if (direction === "upward") {
      minY = Math.min(controlY, baseY - fontSize * 1.5); // top
      maxY = baseY + fontSize * 1.5; // bottom
      svgHeight = maxY - minY + padding * 2;
    } else {
      minY = Math.min(baseY - fontSize * 1.5, baseY); // top
      maxY = Math.max(controlY, baseY + fontSize * 1.5); // bottom
      svgHeight = maxY - minY + padding * 2;
    }

    // Create SVG
    const svg = document.createElementNS(xmlns, "svg");
    svg.setAttribute("width", svgWidth);
    svg.setAttribute("height", svgHeight);
    svg.setAttribute("viewBox", `${0} ${minY - padding} ${svgWidth} ${svgHeight}`);

    svg.style.display = "block";
    svg.style.margin = "0 auto";

    // Path
    const path = document.createElementNS(xmlns, "path");
    const pathId = "archPath" + Math.random().toString(36).slice(2);
    path.setAttribute("id", pathId);
    path.setAttribute("fill", "none");
    path.setAttribute("d", `M0,${baseY} Q${midX},${controlY} ${endX},${baseY}`);
    svg.appendChild(path);

    // Text
    const textElem = document.createElementNS(xmlns, "text");
    textElem.setAttribute("font-size", fontSize);
    textElem.setAttribute("font-family", fontFamily);
    textElem.setAttribute("fill", fontColor);
    textElem.setAttribute("font-weight", fontWeight);
    //if outline applied
    if (container?.getAttribute("data-outline")) {
      textElem.setAttribute("stroke", container?.getAttribute("data-outline-color"));
      textElem.setAttribute("stroke-width", parseInt(container?.getAttribute("data-outline-width")));
    }

    const textPath = document.createElementNS(xmlns, "textPath");
    textPath.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", "#" + pathId);
    textPath.setAttribute("startOffset", "50%");
    textPath.setAttribute("text-anchor", "middle");
    textPath.textContent = text;

    textElem.appendChild(textPath);
    svg.appendChild(textElem);

    container.innerHTML = "";
    container.appendChild(svg);
    container.setAttribute("data-value", sliderValue);
    container.setAttribute("data-transform-type", (direction = "upward" ? "Warp 9" : "Warp 10"));
    container.setAttribute("data-transform-value", sliderValue);
    container.removeAttribute("data-viewBox-value");
    container.style.letterSpacing = "0px";

    updateContext && setTextHtml(document.querySelector(".text-content"));
  };

  const mapSliderValue = (sliderValue, dMin = 1, dMax = 100, aMin = 1, aMax = 500) => {
    return aMin + ((sliderValue - dMin) * (aMax - aMin)) / (dMax - dMin);
  };

  const textArtTransform = (value, sliderValue, updateContext, setTextHtml, setLetterSpacing, availableFontLists) => {
    switch (value) {
      case "Path 1":
        createPlainText(updateContext, setTextHtml, setLetterSpacing);
        break;

      case "Path 2":
        createCircleTextSVG(sliderValue, updateContext, setTextHtml);
        break;

      case "Path 3":
        createHalfCircleTextSVG(sliderValue, false, updateContext, setTextHtml);
        break;

      case "Path 4":
        createHalfCircleTextSVG(sliderValue, true, updateContext, setTextHtml);
        break;

      case "Warp 1":
        createWarpedTextSVG1(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 2":
        createWarpedTextSVG2(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 3":
        createWarpedTextSVG3(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 4":
        createWarpedTextSVG4(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 5":
        createWarpedTextSVG5(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 6":
        createWarpedTextSVG6(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 7":
        createWarpedTextSVG7(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 8":
        createWarpedTextSVG8(sliderValue, updateContext, setTextHtml, availableFontLists);
        break;

      case "Warp 9": {
        let sValue = parseInt(sliderValue);
        let archHeight = mapSliderValue(sValue, 1, 100, 1, 500);
        createArchTextSVG(archHeight, sValue, "upward", updateContext, setTextHtml, availableFontLists);
        break;
      }

      case "Warp 10": {
        let sValue = parseInt(sliderValue);
        let archHeight = mapSliderValue(sValue, 1, 100, 1, 500);
        createArchTextSVG(archHeight, sValue, "downward", updateContext, setTextHtml, availableFontLists);
        break;
      }
    }
  };

  return {
    textArtTransform,
  };
};

export default useTextArtTransform;
